import { StyleProps } from '$/components/ui';
import type { PlayerMode } from '$/components/WebinarPlayer/types';
import { InteractionDetails, InteractionType, ReactionFragment } from '$/GraphQL/schema/types';
import React from 'react';
import * as yup from 'yup';

import { RenderColumnData } from '$/sections/Home/Dashboard/Registrants/types';
import { AnalyticsCardProps } from '../cards/types';
import {
  InteractionInfo,
  InteractionModalFooterProps,
  NoBodyCard,
  OverlayComponentProps,
  VideoOverlayTypes,
} from '../InteractionInfo';
import type { InteractionModalTitleProps } from '../types';
import {
  InteractionAccordionPanelProps,
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
} from '../types';

import { TimelineComponents } from '$/components/WebinarPlayer/context';
import { OverlayInteractionInfo } from '../OverlayInteractionInfo';

import { AnchorIcon } from '$/images/FontAwesomeIcons';
import AnchorOverlay from './AnchorOverlay';
import AnchorCard from './AnchorCard';
import Form from '$/components/ui/Form';

const AnchorSettings = React.lazy(() => import('./AnchorSettings'));
const AnalyticsClickCard = React.lazy(() => import('../cards/AnalyticsClickCard'));

const AnchorSettingsAccordion: (
  props: InteractionModalComponentProps
) => InteractionAccordionPanelProps[] = () => {
  return [
    {
      key: 'Settings',
      active: true,
      title: { content: <Form.AccordionTitle title={'Settings'} /> },
      content: { content: <AnchorSettings /> },
    },
  ];
};

export class AnchorInfo extends OverlayInteractionInfo {
  public typeName(): string | undefined {
    const pause = this.details?.anchor?.pause;
    const includeInAgenda = this.details?.anchor?.includeInAgenda;
    if (includeInAgenda) {
      return pause ? 'Anchor (Agenda item with pause)' : 'Anchor (Agenda item)';
    }
    return pause ? 'Anchor (Pause video)' : 'Anchor';
  }

  /*
  private calcNextShortcut(anchors: InteractionInfo[]): string {
    const possibleShortcuts = '123456789 ';

    let index = 0;
    let shortcut = possibleShortcuts.substr(index++, 1);

    while (anchors.find((i) => i.details?.shortcut === shortcut)) {
      shortcut = possibleShortcuts.substr(index++, 1);
    }

    return shortcut;
  }
  */

  public detailsDefault(interactions: InteractionInfo[]): InteractionDetails {
    const anchors = interactions.filter((i) => i.type === InteractionType.Anchor);
    // const shortcut = this.calcNextShortcut(anchors);
    let itemNo = anchors.length;
    let title = '';

    do {
      itemNo++;
      title = `Agendaitem ${itemNo}`;
    } while (anchors.find((a) => a.title() === title) !== undefined);

    return {
      title,
      anchor: {
        includeInAgenda: false,
        pause: false,
        pauseSecs: 30 * 60,
        pauseMessage: 'Play the video only when you are sure you’re ready to continue!',
      },
    };
  }

  public detailsDuplicateDefault(interactions: InteractionInfo[]): Partial<InteractionDetails> {
    // DD: Removed shortcut for now
    // const anchors = interactions.filter((i) => i.type === InteractionType.Anchor);
    // const shortcut = this.calcNextShortcut(anchors);

    const defaults = this.detailsDefault(interactions);

    return {
      ...super.detailsDuplicateDefault(interactions),
      title: defaults.title,
      shortcut: undefined, // shortcut === ' ' ? undefined : shortcut,
    };
  }

  public renderOverlayComponent(): React.FC<OverlayComponentProps> {
    return AnchorOverlay;
  }

  public userCanAdd(): boolean {
    return true;
  }

  public timelineComponent(): keyof TimelineComponents {
    return 'anchor';
  }

  public category(): 'Standard' | 'Special' | 'Anchor' | 'Conversion' {
    return 'Anchor';
  }

  public hasExpiresInField(): boolean {
    return false;
  }

  public showTimer(_mode: PlayerMode): boolean {
    const endsIn = this.i.details?.endsIn;
    return Boolean(endsIn);
  }

  public hasShowTimerToggle(): boolean {
    return false;
  }

  public hasConditionalDisplay(): boolean {
    return false;
  }

  public supportsShortcut(): boolean {
    return false;
  }

  public onlyOneInstance(): boolean {
    return false;
  }

  public hasResultsAppearAtField(): boolean {
    return false;
  }

  public hasPropertyNameField(): boolean {
    return false;
  }

  public hasPinToTopToggle(): boolean {
    return false;
  }

  public isInteractiveCard(): boolean {
    return false;
  }

  public hasEndsAt(): boolean {
    return false;
  }

  public endsAt(): number {
    // Have play/pause buttons appear only for 1 second
    return this.appearAt() + 1;
  }

  public get endsAtPlaybackPosition(): number {
    return this.playbackPosition + 1;
  }

  public isVisibleToAttendee(): boolean {
    return false;
  }

  public modalValidationRules(): ModalValidationRules {
    const rules = super.modalValidationRules();

    delete rules.endsAt;
    delete rules.overlaySettings;

    return {
      ...rules,
      title: yup
        .string()
        .trim()
        .when('anchor.includeInAgenda', ([value], field) => {
          return value ? field.required() : field.nullable();
        }),
      description: yup.string().nullable(),
      anchor: yup.object().shape({
        pause: yup.boolean().required(),
        pauseSecs: yup
          .number()
          .nullable()
          .when('pause', {
            is: true,
            then: () => {
              return yup
                .number()
                .required()
                .min(10, 'Pause minimum is 10 seconds')
                .max(30 * 60, 'Pause maximum is 30 minutes')
                .required('You must have a time limit');
            },
          }),
        includeInAgenda: yup.boolean().required(),
      }),
    };
  }

  public attendeeCardValidationRules(): yup.AnyObjectSchema | undefined {
    return undefined;
  }

  public icon(): React.FC<StyleProps> {
    return AnchorIcon;
  }

  public hasCard(_isPreview?: boolean): boolean {
    return false;
  }

  public hasVideoOverlay(): VideoOverlayTypes | null {
    return 'invisible';
  }

  public isEditable(): boolean {
    return true;
  }

  public detailsKey(): string | undefined {
    return undefined;
  }

  // Does the interaction generate Answers
  // (Click doesn't count as its simply a reaction not an answer)
  public hasAnswers(): boolean {
    return false;
  }

  // Where does primary answer live?
  public answerMessage(
    _reaction: Pick<ReactionFragment, 'stringAnswer' | 'numberAnswer' | 'detailsFields'>
  ): string {
    return '';
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return null;
  }

  public modalTitleComponent(): React.FC<InteractionModalTitleProps> | null {
    return null;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook | null {
    return AnchorSettingsAccordion;
  }

  public modalFooterComponent(): React.FC<InteractionModalFooterProps> | null {
    return null;
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsClickCard;
  }

  public cardComponent(): React.FC<{ onClickHeader?: () => void }> {
    return AnchorCard;
  }

  public cardBodyComponent(): React.FC {
    return NoBodyCard;
  }

  // Wants to show results
  public hasResultsToShow(): boolean {
    return false;
  }

  public hasEngagement(): boolean {
    return false;
  }

  public renderColumnData(_data: RenderColumnData): RenderColumnData | null {
    return null;
  }

  public onlyExistsInPresentationRoom(): boolean {
    return true;
  }
}
